import { Box, Flex } from '@chakra-ui/react';
import {
  DragDropContext,
  Droppable,
  Draggable,
} from '@atlaskit/pragmatic-drag-and-drop-react-beautiful-dnd-migration';
import { Alert, Badge, Text, Upload } from '@kula-ai/components';
import {
  CheckCircle,
  DotsGrid,
  Download01,
  Eye,
  File04,
  InfoCircle,
  Trash01,
} from '@kula-ai/icons-core';
import {
  ESIGN_ACTIVITY_COPY,
  ESIGN_DOCUMENT_LIMITS,
} from '@/constants/esignature';
import type { EsignWizardDocument } from '@/types/esignature';
import { EsignTokensPanel } from './EsignTokensPanel';
import { isDocumentInvalid } from './esignDocuments';

const LEARN_MORE_URL =
  'https://support.kula.ai/articles/4341127242-creating-and-sending-assessments-as-a-recruiter';

interface DocumentRowProps {
  document: EsignWizardDocument;
  index: number;
  isReorderLocked: boolean;
  onPreview: (document: EsignWizardDocument) => void;
  onDownload: (document: EsignWizardDocument) => void;
  onRemove: (key: string) => void;
}

const DocumentRow = ({
  document,
  index,
  isReorderLocked,
  onPreview,
  onDownload,
  onRemove,
}: DocumentRowProps) => {
  const isInvalid = isDocumentInvalid(document);
  const hasVerdict = Boolean(document.validation);

  return (
    <Draggable
      draggableId={document.key}
      index={index}
      isDragDisabled={isReorderLocked}
    >
      {(provided) => (
        <Flex
          ref={provided.innerRef}
          {...provided.draggableProps}
          alignItems="center"
          gap="12px"
          p="12px 24px 12px 12px"
          bg={isInvalid ? 'pastel-1-light' : 'white'}
          border="1px solid"
          borderColor={isInvalid ? 'error-300' : 'border-color'}
          borderRadius="6px"
          // Figma's File component (2:4541) sits on Shadow Bottom, which the
          // theme already carries verbatim as drop-shadows-bottom.
          boxShadow="drop-shadows-bottom"
          data-test-id={`esign-document-${document.filename}`}
        >
          <Box
            {...provided.dragHandleProps}
            display="flex"
            alignItems="center"
            color="secondary-700"
            opacity={isReorderLocked ? 0.4 : 1}
            cursor={isReorderLocked ? 'default' : 'grab'}
            aria-label={`Reorder ${document.filename}`}
          >
            <DotsGrid size={16} />
          </Box>
          <Flex
            align="center"
            justify="center"
            w="44px"
            h="44px"
            flexShrink={0}
            borderRadius="6px"
            bg={isInvalid ? 'pastel-1-light' : 'secondary-50'}
            // Chakra tokens, not `--kula-colors-*` vars: neither name is among
            // the seven that exist, so the var form rendered nothing. The icon
            // inside picks this up through `currentColor`.
            color={isInvalid ? 'error-500' : 'secondary-700'}
          >
            <File04 size={24} />
          </Flex>
          <Box flex="1" minW={0} overflow="hidden">
            <Text
              variant="p"
              color={isInvalid ? 'error' : undefined}
              truncate
              tooltip
            >
              {document.filename}
            </Text>
          </Box>
          {hasVerdict &&
            (isInvalid ? (
              // Figma: Badge `Type=Filled, Color=Red`. That variant paints
              // #E4626F with white text only because tailwind.config.js now
              // scans kula's dist — without it the surface is transparent and
              // the chip reads white-on-white. Asserted in the browser spec
              // beside this file so it cannot regress silently.
              <Badge
                type="filled"
                color="red"
                leftElement={<InfoCircle size={14} />}
              >
                {ESIGN_ACTIVITY_COPY.STATUS_INVALID}
              </Badge>
            ) : (
              <Badge
                type="default"
                color="green"
                leftElement={<CheckCircle size={14} />}
              >
                {ESIGN_ACTIVITY_COPY.STATUS_VERIFIED}
              </Badge>
            ))}
          <Flex align="center" gap="12px" flexShrink={0} color="secondary-700">
            <Box
              as="button"
              type="button"
              aria-label={`Preview ${document.filename}`}
              display="flex"
              cursor="pointer"
              onClick={() => onPreview(document)}
            >
              <Eye size={16} />
            </Box>
            <Box
              as="button"
              type="button"
              aria-label={`Download ${document.filename}`}
              display="flex"
              cursor="pointer"
              onClick={() => onDownload(document)}
            >
              <Download01 size={16} />
            </Box>
            <Box
              as="button"
              type="button"
              aria-label={`Remove ${document.filename}`}
              display="flex"
              cursor="pointer"
              onClick={() => onRemove(document.key)}
            >
              <Trash01 size={16} />
            </Box>
          </Flex>
        </Flex>
      )}
    </Draggable>
  );
};

export interface EsignDocumentsStepProps {
  documents: EsignWizardDocument[];
  isValidating: boolean;
  onFilesAdded: (files: File[]) => void;
  onRemove: (key: string) => void;
  onReorder: (fromIndex: number, toIndex: number) => void;
  onPreview: (document: EsignWizardDocument) => void;
  onDownload: (document: EsignWizardDocument) => void;
}

/**
 * Step 1: upload the .docx documents and see what tokens they carry.
 *
 * Why a document set is blocked is conveyed by the rows themselves — the red
 * row and its Invalid tokens chip — and by the disabled Next button, matching
 * the design. The set-level rule (no document carries the candidate signature
 * anchor) is still enforced, but it has no surface here and needs one from
 * design; see getDocumentsBlocker.
 */
export const EsignDocumentsStep = ({
  documents,
  isValidating,
  onFilesAdded,
  onRemove,
  onReorder,
  onPreview,
  onDownload,
}: EsignDocumentsStepProps) => {
  const handleDragEnd = (result: {
    source?: { index: number };
    destination?: { index: number } | null;
  }) => {
    const from = result?.source?.index;
    const to = result?.destination?.index;
    if (from === undefined || to === undefined || from === to) return;
    onReorder(from, to);
  };

  // Content slot pads 24px on the left only; the document column adds its own
  // 16px top and 24px right, leaving the rail flush to the drawer's right edge
  // (Figma 7:4983).
  return (
    <Flex flex="1" minH={0} overflow="hidden" pl="24px">
      <Flex
        flex="1"
        minW={0}
        flexDir="column"
        gap="12px"
        pt="16px"
        pr="24px"
        overflowY="auto"
      >
        <Alert
          status="neutral"
          variant="subtle"
          // Figma's banner (7:4983) is a plain padded panel: title, body, link,
          // no status glyph. Alert leads with one unless it is turned off.
          icon={false}
          // kula rounds to 8px; every surface in this drawer is 6px.
          className="!rounded-md"
          // Alert renders title and body in the same muted tone. The design
          // pitches the title darker (secondary-900) against a secondary-700
          // body, so both are passed as nodes rather than strings.
          title={
            <Text className="!font-semibold !text-secondary-900">
              {ESIGN_ACTIVITY_COPY.HOW_IT_WORKS_TITLE}
            </Text>
          }
          description={
            <Text className="!text-secondary-700">
              {ESIGN_ACTIVITY_COPY.HOW_IT_WORKS_BODY}
            </Text>
          }
        >
          {/* The banner's link is the plain Link Button in Figma (2:4250), not
              the underlined one the dropzone uses (2:4254). */}
          <Box
            as="a"
            href={LEARN_MORE_URL}
            target="_blank"
            rel="noreferrer"
            display="inline-block"
          >
            <Text className="!font-semibold !text-primary-600">
              {ESIGN_ACTIVITY_COPY.LEARN_MORE}
            </Text>
          </Box>
        </Alert>

        <Upload
          multiple
          variant="dropzone"
          size="md"
          accept=".docx"
          maxFiles={ESIGN_DOCUMENT_LIMITS.MAX_FILES}
          maxSize={ESIGN_DOCUMENT_LIMITS.MAX_SIZE_BYTES}
          mainText={ESIGN_ACTIVITY_COPY.UPLOAD_MAIN}
          linkText={ESIGN_ACTIVITY_COPY.UPLOAD_LINK}
          description={ESIGN_ACTIVITY_COPY.UPLOAD_HELPER}
          onFilesAdded={onFilesAdded}
          disabled={isValidating}
          // Figma dashes the dropzone in #9FAAFF (primary-300) at a 6px radius
          // and 143px tall. kula defaults to a grey border, an 8px radius and
          // roomier internal spacing, and exposes no tone or density prop, so
          // the vertical padding is trimmed to land on the design's height. A
          // `tone`/`size` on Upload would be the better home for this upstream.
          classNames={{
            // Everything below is a design mismatch kula exposes no prop for.
            // Surface: a 2px grey dash at an 8px radius with a 160px floor,
            // against Figma's 1px #9FAAFF at 6px and 143px tall. Copy: a muted
            // 14px lead over a 12px helper, against a secondary-900 semibold
            // lead over a 14px secondary-700 helper. The child selectors lean
            // on Upload's DOM (an icon, a lead `p` wrapping the link `span`,
            // then a description `p`), so they are pinned by a browser spec.
            // Tone, density and text slots on Upload would retire all of it.
            dropzone: [
              '!border !border-primary-300 !rounded-md !py-8 !min-h-0',
              '[&>p]:!text-sm',
              '[&>p:first-of-type]:!font-semibold',
              '[&>p:first-of-type]:!text-secondary-900',
              '[&>p:last-of-type]:!text-secondary-700',
              '[&_span]:!text-primary-600',
            ].join(' '),
          }}
          // The document list is rendered below rather than through Upload's own
          // `files` slot: its row has nowhere to put the Verified / Invalid
          // tokens badge, which is the whole point of this step. Worth raising
          // upstream as a status slot on UploadFileItem in @kula-ai/components.
          files={[]}
        />

        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="esign-documents">
            {(provided) => (
              <Flex
                ref={provided.innerRef}
                {...provided.droppableProps}
                flexDir="column"
                gap="6px"
                data-test-id="esign-document-list"
              >
                {documents.map((document, index) => (
                  <DocumentRow
                    key={document.key}
                    document={document}
                    index={index}
                    isReorderLocked={isValidating}
                    onPreview={onPreview}
                    onDownload={onDownload}
                    onRemove={onRemove}
                  />
                ))}
                {provided.placeholder}
              </Flex>
            )}
          </Droppable>
        </DragDropContext>
      </Flex>

      <EsignTokensPanel documents={documents} />
    </Flex>
  );
};
